import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Loader2, AlertCircle, Settings, ChevronDown, Sparkles, Network, Globe, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useReportingMode } from '../../context/ReportingModeContext';

function estimateRuntime(agentCount, socialEnabled, rounds) {
  if (!agentCount || agentCount < 1) return null;
  // AI response generation: ~15 agents/sec with batching + concurrency
  const aiSeconds = agentCount / 15;
  // SIDE: graph construction (~1.4ms/agent) + diffusion (~0.5ms/agent/round)
  const sideSeconds = socialEnabled ? (agentCount * 1.4 / 1000) + (rounds * agentCount * 0.0005) : 0;
  const total = aiSeconds + sideSeconds;
  if (total < 30) return { label: "< 30 sec", level: "fast" };
  if (total < 90) return { label: "~1 min", level: "fast" };
  if (total < 180) return { label: "~2-3 min", level: "moderate" };
  if (total < 420) return { label: "~5-7 min", level: "moderate" };
  if (total < 900) return { label: "~10-15 min", level: "slow" };
  if (total < 1800) return { label: "~20-30 min", level: "slow" };
  return { label: "30+ min", level: "slow" };
}

const SIDE_PRESETS = {
  low: { label: "Low Conformity", rounds: 2, alpha: 0.15, confidence_radius: 0.25, noise_sigma: 0.02, backfire_prob: 0.0, backfire_gamma: 0.03 },
  medium: { label: "Medium (Default)", rounds: 2, alpha: 0.25, confidence_radius: 0.35, noise_sigma: 0.015, backfire_prob: 0.08, backfire_gamma: 0.05 },
  high: { label: "High Conformity", rounds: 3, alpha: 0.4, confidence_radius: 0.5, noise_sigma: 0.01, backfire_prob: 0.1, backfire_gamma: 0.06 },
};

const DEFAULT_SIMILARITY_WEIGHTS = { dept: 0.3, location: 0.2, age_band: 0.2, tenure_band: 0.15, opinion: 0.15 };

export function CreateSimulationDialog({
  open, onOpenChange,
  populations, stimuli, activeClient,
  user, selectedClientId,
  onCreated,
  api,
}) {
  const [name, setName] = useState('');
  const [populationId, setPopulationId] = useState('');
  const [stimulusId, setStimulusId] = useState('');
  const [sampleSize, setSampleSize] = useState('');
  const [formTouched, setFormTouched] = useState({ name: false, populationId: false, stimulusId: false });
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [batchSize, setBatchSize] = useState(400);
  const [concurrencyLimit, setConcurrencyLimit] = useState(15);
  const [usePre, setUsePre] = useState(true);

  // Social Interaction Engine state
  const [socialEnabled, setSocialEnabled] = useState(false);
  const [socialPreset, setSocialPreset] = useState('medium');
  const [socialRounds, setSocialRounds] = useState(SIDE_PRESETS.medium.rounds);
  const [socialNetworkType, setSocialNetworkType] = useState('homophily_small_world');
  const [socialAlpha, setSocialAlpha] = useState(SIDE_PRESETS.medium.alpha);
  const [socialSusceptibility, setSocialSusceptibility] = useState('uniform');
  const [socialInteractionsPerRound, setSocialInteractionsPerRound] = useState(3);
  const [socialConfidenceRadius, setSocialConfidenceRadius] = useState(SIDE_PRESETS.medium.confidence_radius);
  const [socialNoiseSigma, setSocialNoiseSigma] = useState(SIDE_PRESETS.medium.noise_sigma);
  const [socialBeta, setSocialBeta] = useState(0.4);
  const [socialBackfireProb, setSocialBackfireProb] = useState(SIDE_PRESETS.medium.backfire_prob);
  const [socialBackfireGamma, setSocialBackfireGamma] = useState(SIDE_PRESETS.medium.backfire_gamma);
  const [socialNeighborsK, setSocialNeighborsK] = useState(12);
  const [socialRewireProb, setSocialRewireProb] = useState(0.05);
  const [socialAdvancedOpen, setSocialAdvancedOpen] = useState(false);

  // Web Context Exposure
  const [exposureEnabled, setExposureEnabled] = useState(false);
  const [exposureRate, setExposureRate] = useState(70);

  // Messaging Framing
  const [messagingFraming, setMessagingFraming] = useState('none');

  // Context Influence Control
  const [contextMode, setContextMode] = useState('standard');
  const [contextMaxChars, setContextMaxChars] = useState(800);

  // Module Type
  const { enabledModules } = useReportingMode();
  const [moduleType, setModuleType] = useState('');

  const selectedPopulation = populations?.find(p => p.id === populationId);

  const runtimeEstimate = useMemo(() => {
    const count = sampleSize ? parseInt(sampleSize) : selectedPopulation?.row_count;
    return estimateRuntime(count, socialEnabled, socialRounds);
  }, [sampleSize, selectedPopulation?.row_count, socialEnabled, socialRounds]);

  const resetForm = () => {
    setName(''); setPopulationId(''); setStimulusId(''); setSampleSize('');
    setBatchSize(400); setConcurrencyLimit(15); setUsePre(true); setAdvancedOpen(false);
    setSocialEnabled(false); setSocialRounds(3); setSocialNetworkType('hybrid');
    setSocialAlpha(0.3); setSocialSusceptibility('uniform');
    setExposureEnabled(false); setExposureRate(70);
    setMessagingFraming('none'); setModuleType('');
    setContextMode('standard'); setContextMaxChars(800);
    setFormTouched({ name: false, populationId: false, stimulusId: false });
  };

  const applySidePreset = (presetKey) => {
    const preset = SIDE_PRESETS[presetKey] || SIDE_PRESETS.medium;
    setSocialPreset(presetKey);
    setSocialRounds(preset.rounds); setSocialAlpha(preset.alpha);
    setSocialConfidenceRadius(preset.confidence_radius); setSocialNoiseSigma(preset.noise_sigma);
    setSocialBackfireProb(preset.backfire_prob); setSocialBackfireGamma(preset.backfire_gamma);
  };

  const handleCreate = async (e, andRun = false) => {
    if (e) e.preventDefault();
    if (user?.is_admin && (!selectedClientId || selectedClientId === 'all')) return;
    setSaving(true);
    try {
      const payload = {
        name, population_id: populationId, stimulus_id: stimulusId,
        sample_size: sampleSize ? parseInt(sampleSize) : null,
        config: {
          batch_size: batchSize, concurrency_limit: concurrencyLimit, use_pre: usePre,
          context_mode: contextMode,
          context_max_chars: contextMaxChars,
          ...(messagingFraming !== 'none' ? { messaging_framing: messagingFraming } : {}),
        },
        social_enabled: socialEnabled,
        social_config: socialEnabled ? {
          preset: socialPreset, rounds: socialRounds, network_type: socialNetworkType,
          alpha: socialAlpha, susceptibility_mode: socialSusceptibility,
          interactions_per_round: socialInteractionsPerRound, confidence_radius: socialConfidenceRadius,
          beta: socialBeta, noise_sigma: socialNoiseSigma,
          backfire_prob: socialBackfireProb, backfire_gamma: socialBackfireGamma,
          neighbors_k: socialNeighborsK, rewire_prob: socialRewireProb,
          similarity_weights: DEFAULT_SIMILARITY_WEIGHTS,
        } : null,
        web_context_exposure_rate: exposureEnabled ? exposureRate / 100 : null,
        module_type: moduleType || (enabledModules?.length === 1 ? enabledModules[0] : null),
      };
      const url = user?.is_admin && selectedClientId && selectedClientId !== 'all'
        ? `/simulations?client_id=${selectedClientId}` : `/simulations`;
      const response = await api.post(url, payload);

      if (andRun && response.data?.id) {
        try {
          await api.post(`/simulations/${response.data.id}/run`);
          const { toast } = await import('sonner');
          toast.success('Simulation created and started');
        } catch {
          const { toast } = await import('sonner');
          toast.warning('Simulation created but failed to start. Run it manually.');
        }
      }
      onOpenChange(false);
      resetForm();
      onCreated();
    } catch (error) {
      const { toast } = await import('sonner');
      toast.error(error.response?.data?.detail || 'Failed to create simulation');
    } finally {
      setSaving(false);
    }
  };

  // Prevent Enter from submitting the form
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Simulation</DialogTitle>
          <DialogDescription>Configure a new population behavior simulation</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreate} onKeyDown={handleKeyDown} className="space-y-4 mt-2">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="sim-name">Simulation Name <span className="text-red-500">*</span></Label>
            <Input id="sim-name" value={name} onChange={(e) => setName(e.target.value)}
              onBlur={() => setFormTouched(prev => ({ ...prev, name: true }))}
              placeholder="e.g., Q4 Policy Test - All Staff"
              className={cn(formTouched.name && !name && "border-red-300 focus-visible:ring-red-500")}
              data-testid="simulation-name-input" />
            {formTouched.name && !name && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Simulation name is required</p>}
          </div>

          {/* Population */}
          <div className="space-y-2">
            <Label>Population <span className="text-red-500">*</span></Label>
            <Select value={populationId} onValueChange={(v) => { setPopulationId(v); setFormTouched(prev => ({ ...prev, populationId: true })); }}>
              <SelectTrigger data-testid="population-select" className={cn(formTouched.populationId && !populationId && "border-red-300 focus:ring-red-500")} onBlur={() => setFormTouched(prev => ({ ...prev, populationId: true }))}>
                <SelectValue placeholder="Select population" />
              </SelectTrigger>
              <SelectContent>{populations.map(pop => <SelectItem key={pop.id} value={pop.id}>{pop.name} ({pop.row_count} individuals)</SelectItem>)}</SelectContent>
            </Select>
            {formTouched.populationId && !populationId && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Please select a population</p>}
          </div>

          {/* Stimulus */}
          <div className="space-y-2">
            <Label>Stimulus <span className="text-red-500">*</span></Label>
            <Select value={stimulusId} onValueChange={(v) => { setStimulusId(v); setFormTouched(prev => ({ ...prev, stimulusId: true })); }}>
              <SelectTrigger data-testid="stimulus-select" className={cn(formTouched.stimulusId && !stimulusId && "border-red-300 focus:ring-red-500")} onBlur={() => setFormTouched(prev => ({ ...prev, stimulusId: true }))}>
                <SelectValue placeholder="Select stimulus" />
              </SelectTrigger>
              <SelectContent>{stimuli.map(stim => <SelectItem key={stim.id} value={stim.id}>{stim.name}</SelectItem>)}</SelectContent>
            </Select>
            {formTouched.stimulusId && !stimulusId && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Please select a stimulus</p>}
          </div>

          {/* Sample Size */}
          <div className="space-y-2">
            <Label htmlFor="sample-size">Sample Size (Optional)</Label>
            <Input id="sample-size" type="number" value={sampleSize} onChange={(e) => setSampleSize(e.target.value)}
              placeholder={selectedPopulation ? `Max: ${selectedPopulation.row_count}` : 'Leave empty for all'}
              min={1} max={selectedPopulation?.row_count} data-testid="sample-size-input" />
            <p className="text-xs text-gray-500">Leave empty to simulate all individuals</p>
          </div>

          {/* Module Type - Only show if client has multiple modules */}
          {enabledModules?.length > 1 && (
            <div className="space-y-2">
              <Label>Report Module</Label>
              <Select value={moduleType || enabledModules[0]} onValueChange={setModuleType}>
                <SelectTrigger data-testid="module-type-select">
                  <SelectValue placeholder="Select module" />
                </SelectTrigger>
                <SelectContent>
                  {enabledModules.includes('people_lab') && <SelectItem value="people_lab">PeopleLab - HR simulations</SelectItem>}
                  {enabledModules.includes('market_lab') && <SelectItem value="market_lab">MarketLab - Marketing research</SelectItem>}
                  {enabledModules.includes('pharma_lab') && <SelectItem value="pharma_lab">PharmaLab - Pharma / healthcare</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Determines which reporting template is used</p>
            </div>
          )}

          {/* Social Interaction Engine */}
          {activeClient?.social_interactions_enabled && (
            <div className="border border-blue-200 rounded-lg p-4 space-y-4 bg-blue-50/30" data-testid="social-config-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Network className="w-4 h-4 text-blue-600" /><Label className="text-sm font-medium">Social Interactions</Label></div>
                <Switch checked={socialEnabled} onCheckedChange={setSocialEnabled} className="data-[state=checked]:bg-primary" data-testid="social-enabled-toggle" />
              </div>
              {socialEnabled && (
                <>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Social simulation models peer influence dynamics. Results represent simulated interaction effects.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">SIDE Preset</Label>
                      <Select value={socialPreset} onValueChange={applySidePreset}>
                        <SelectTrigger className="h-8 text-xs" data-testid="social-preset-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(SIDE_PRESETS).map(([key, p]) => <SelectItem key={key} value={key}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Network Type</Label>
                      <Select value={socialNetworkType} onValueChange={setSocialNetworkType}>
                        <SelectTrigger className="h-8 text-xs" data-testid="social-network-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="homophily_small_world">Homophily Small-World (default)</SelectItem>
                          <SelectItem value="random">Random</SelectItem>
                          <SelectItem value="demographic">Demographic-based</SelectItem>
                          <SelectItem value="hierarchical">Hierarchical</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Rounds ({socialRounds})</Label><Slider value={[socialRounds]} onValueChange={([v]) => setSocialRounds(v)} min={1} max={5} step={1} data-testid="social-rounds-slider" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Influence ({socialAlpha.toFixed(2)})</Label><Slider value={[socialAlpha * 100]} onValueChange={([v]) => setSocialAlpha(v / 100)} min={0} max={100} step={5} data-testid="social-alpha-slider" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Confidence Radius ({socialConfidenceRadius.toFixed(2)})</Label><Slider value={[socialConfidenceRadius * 100]} onValueChange={([v]) => setSocialConfidenceRadius(v / 100)} min={10} max={60} step={5} data-testid="social-confidence-radius" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Interactions/Round ({socialInteractionsPerRound})</Label><Slider value={[socialInteractionsPerRound]} onValueChange={([v]) => setSocialInteractionsPerRound(v)} min={1} max={4} step={1} data-testid="social-interactions-per-round" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Susceptibility</Label>
                      <Select value={socialSusceptibility} onValueChange={setSocialSusceptibility}>
                        <SelectTrigger className="h-8 text-xs" data-testid="social-susceptibility-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="uniform">Uniform</SelectItem><SelectItem value="derived">Derived from personality</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Noise ({socialNoiseSigma.toFixed(3)})</Label><Slider value={[socialNoiseSigma * 1000]} onValueChange={([v]) => setSocialNoiseSigma(v / 1000)} min={5} max={30} step={5} data-testid="social-noise-sigma" /></div>
                  </div>
                  <Collapsible open={socialAdvancedOpen} onOpenChange={setSocialAdvancedOpen}>
                    <CollapsibleTrigger className="text-xs text-blue-700 hover:text-blue-900" data-testid="social-advanced-toggle">{socialAdvancedOpen ? 'Hide advanced' : 'Show advanced'}</CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label className="text-xs">Backfire Prob ({socialBackfireProb.toFixed(2)})</Label><Slider value={[socialBackfireProb * 100]} onValueChange={([v]) => setSocialBackfireProb(v / 100)} min={0} max={20} step={2} data-testid="social-backfire-prob" /></div>
                        <div className="space-y-1.5"><Label className="text-xs">Backfire ({socialBackfireGamma.toFixed(2)})</Label><Slider value={[socialBackfireGamma * 100]} onValueChange={([v]) => setSocialBackfireGamma(v / 100)} min={1} max={12} step={1} data-testid="social-backfire-gamma" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label className="text-xs">Neighbors k ({socialNeighborsK})</Label><Slider value={[socialNeighborsK]} onValueChange={([v]) => setSocialNeighborsK(v)} min={6} max={20} step={1} data-testid="social-neighbors-k" /></div>
                        <div className="space-y-1.5"><Label className="text-xs">Rewire p ({socialRewireProb.toFixed(2)})</Label><Slider value={[socialRewireProb * 100]} onValueChange={([v]) => setSocialRewireProb(v / 100)} min={0} max={20} step={1} data-testid="social-rewire-prob" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label className="text-xs">Conviction ({socialBeta.toFixed(2)})</Label><Slider value={[socialBeta * 100]} onValueChange={([v]) => setSocialBeta(v / 100)} min={10} max={80} step={5} data-testid="social-beta" /></div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </>
              )}
            </div>
          )}

          {/* Web Context Exposure */}
          {(() => {
            const selectedStim = stimuli?.find(s => s.id === stimulusId);
            if (!selectedStim?.web_context_pack_id) return null;
            return (
              <div className="border border-blue-200 rounded-lg p-4 space-y-3 bg-blue-50/30" data-testid="exposure-config-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-blue-600" /><Label className="text-sm font-medium">Variable Information Exposure</Label></div>
                  <Switch checked={exposureEnabled} onCheckedChange={setExposureEnabled} className="data-[state=checked]:bg-primary" data-testid="exposure-toggle" />
                </div>
                {exposureEnabled && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Exposure Rate: {exposureRate}%</Label><span className="text-[10px] text-muted-foreground">{exposureRate}% exposed / {100 - exposureRate}% control</span></div>
                    <Slider value={[exposureRate]} onValueChange={([v]) => setExposureRate(v)} min={10} max={90} step={5} data-testid="exposure-rate-slider" />
                    <p className="text-xs text-muted-foreground">Only {exposureRate}% of agents will receive web context. Compare responses between exposed and control groups in Analysis.</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Messaging Framing */}
          <div className="border border-blue-200 rounded-lg p-4 space-y-3 bg-blue-50/30" data-testid="messaging-framing-section">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <Label className="text-sm font-medium">Messaging Sensitivity</Label>
            </div>
            <p className="text-xs text-muted-foreground -mt-1">Apply conditional probability weighting based on message framing strategy. Segments respond differently depending on the framing.</p>
            <Select value={messagingFraming} onValueChange={setMessagingFraming}>
              <SelectTrigger className="h-9" data-testid="messaging-framing-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Framing (Default)</SelectItem>
                <SelectItem value="scientific">Scientific-Heavy Framing</SelectItem>
                <SelectItem value="patient_outcome">Patient-Outcome Framing</SelectItem>
                <SelectItem value="innovation">Innovation Framing</SelectItem>
              </SelectContent>
            </Select>
            {messagingFraming !== 'none' && (
              <p className="text-[10px] text-blue-700 bg-blue-100 rounded p-2">
                {messagingFraming === 'scientific' && 'Evidence-sensitive and research-oriented segments will respond more favorably. Traditional/skeptical segments may resist.'}
                {messagingFraming === 'patient_outcome' && 'Care-oriented and patient-focused segments will respond more favorably. Business/executive-focused segments may be less responsive.'}
                {messagingFraming === 'innovation' && 'Digital-savvy and early-adopter segments will respond more favorably. Traditional/conservative segments may resist.'}
              </p>
            )}
          </div>

          {/* Advanced Settings */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="border border-gray-200 rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg">
              <span className="flex items-center gap-2"><Settings className="w-4 h-4" />Advanced Settings</span>
              <ChevronDown className={cn("w-4 h-4 transition-transform", advancedOpen && "rotate-180")} />
            </CollapsibleTrigger>
            <CollapsibleContent className="p-4 pt-0 space-y-4 border-t border-gray-200">
              <div className="pt-3 pb-2 border-b border-gray-100">
                <div className="flex items-start space-x-3">
                  <Checkbox id="use-pre" checked={usePre} onCheckedChange={setUsePre} data-testid="use-pre-checkbox" />
                  <div className="grid gap-1.5 leading-none flex-1">
                    <label htmlFor="use-pre" className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-blue-600" />Enable PRE Mode (Recommended)
                    </label>
                    <p className="text-xs text-gray-500 leading-relaxed">Probabilistic Response Engine: Generates probability distributions for each response, enabling advanced analytics. <span className="font-medium">Slightly slower but provides richer insights.</span></p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs">Batch Size</Label><Input type="number" value={batchSize} onChange={(e) => setBatchSize(parseInt(e.target.value) || 400)} min={10} max={1000} className="h-9" data-testid="batch-size-input" /></div>
                <div className="space-y-2"><Label className="text-xs">Concurrency</Label><Input type="number" value={concurrencyLimit} onChange={(e) => setConcurrencyLimit(parseInt(e.target.value) || 15)} min={1} max={50} className="h-9" data-testid="concurrency-input" /></div>
              </div>

              {/* Context Influence Control */}
              <div className="pt-3 border-t border-gray-100 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Context Influence</Label>
                  <span className="text-[10px] text-muted-foreground">how much stimulus & org context shapes answers</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mode</Label>
                    <Select value={contextMode} onValueChange={setContextMode}>
                      <SelectTrigger className="h-9" data-testid="context-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard (default)</SelectItem>
                        <SelectItem value="light">Light — background only</SelectItem>
                        <SelectItem value="none">None — demographics only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Max characters ({contextMaxChars})</Label>
                    <Slider
                      value={[contextMaxChars]}
                      onValueChange={([v]) => setContextMaxChars(v)}
                      min={200}
                      max={2000}
                      step={100}
                      disabled={contextMode === 'none'}
                      data-testid="context-max-chars-slider"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  {contextMode === 'standard' && 'Stimulus context and organizational background are used as decision conditioning.'}
                  {contextMode === 'light' && 'Context is passed as background only with an explicit disclaimer — demographics drive the answer.'}
                  {contextMode === 'none' && 'Only the stimulus prompt and demographic profile are sent to the model.'}
                </p>
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800"><p className="text-xs text-blue-800 dark:text-blue-300"><strong>Note:</strong> Temperature distribution and AI model settings are configured at the client level by your administrator.</p></div>
            </CollapsibleContent>
          </Collapsible>

          {runtimeEstimate && populationId && (
            <div className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-xs",
              runtimeEstimate.level === "fast" && "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800",
              runtimeEstimate.level === "moderate" && "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
              runtimeEstimate.level === "slow" && "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800",
            )} data-testid="runtime-estimate">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span>Estimated runtime: <strong>{runtimeEstimate.label}</strong>{socialEnabled && " (incl. social interactions)"}</span>
            </div>
          )}

          <DialogFooter className="gap-2 pt-2 flex-col sm:flex-row">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <div className="flex gap-2">
              <Button type="submit" variant="outline" disabled={saving || !name || !populationId || !stimulusId}
                onClick={() => setFormTouched({ name: true, populationId: true, stimulusId: true })}
                data-testid="submit-simulation-btn">
                {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : 'Create Only'}
              </Button>
              <Button type="button" disabled={saving || !name || !populationId || !stimulusId}
                onClick={(e) => { setFormTouched({ name: true, populationId: true, stimulusId: true }); handleCreate(e, true); }}
                className={cn("bg-primary hover:bg-primary/90", (!name || !populationId || !stimulusId) && "opacity-70")}
                data-testid="submit-and-run-simulation-btn">
                {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : 'Create & Run'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
